import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Bold, Italic, Underline, List, ListOrdered, Link, Quote, Code, Image,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  minRows?: number;
}

const TOOLBAR_ITEMS = [
  { icon: Bold, tag: "**", wrap: true, label: "Bold" },
  { icon: Italic, tag: "*", wrap: true, label: "Italic" },
  { icon: Underline, tag: "__", wrap: true, label: "Underline" },
  { icon: Code, tag: "`", wrap: true, label: "Code" },
  { icon: Quote, tag: "> ", wrap: false, label: "Quote" },
  { icon: List, tag: "- ", wrap: false, label: "Bullet list" },
  { icon: ListOrdered, tag: "1. ", wrap: false, label: "Numbered list" },
  { icon: Link, tag: "[text](url)", wrap: false, label: "Link", special: "link" },
  { icon: Image, tag: "![alt](url)", wrap: false, label: "Image", special: "image" },
] as const;

export function ForumRichEditor({ value, onChange, placeholder = "Write something...", minRows = 6 }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertFormatting = useCallback((item: (typeof TOOLBAR_ITEMS)[number] & { special?: string }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let newText: string;
    let cursorOffset: number;

    if (item.special === "link") {
      const linkText = selectedText || "link text";
      newText = value.substring(0, start) + `[${linkText}](url)` + value.substring(end);
      cursorOffset = start + linkText.length + 3;
    } else if (item.special === "image") {
      newText = value.substring(0, start) + `![description](image-url)` + value.substring(end);
      cursorOffset = start + 15;
    } else if (item.wrap) {
      const wrapped = `${item.tag}${selectedText || "text"}${item.tag}`;
      newText = value.substring(0, start) + wrapped + value.substring(end);
      cursorOffset = start + item.tag.length + (selectedText ? selectedText.length : 4) + item.tag.length;
    } else {
      // Line prefix
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      newText = value.substring(0, lineStart) + item.tag + value.substring(lineStart);
      cursorOffset = start + item.tag.length;
    }

    onChange(newText);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorOffset, cursorOffset);
    });
  }, [value, onChange]);

  return (
    <div className="rounded-md border border-border overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-muted/40 border-b border-border">
        {TOOLBAR_ITEMS.map((item) => (
          <Button
            key={item.label}
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title={item.label}
            aria-label={item.label}
            onClick={(e) => {
              e.preventDefault();
              insertFormatting(item);
            }}
          >
            <item.icon className="h-3.5 w-3.5" />
          </Button>
        ))}
        <span className="ml-auto text-[10px] text-muted-foreground">Markdown supported</span>
      </div>

      {/* Editor area */}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={minRows}
        maxLength={10000}
        className="border-0 rounded-none focus-visible:ring-0 resize-y bg-background"
      />
    </div>
  );
}
